import { useCallback, useEffect, useRef, useState } from "react";
import { GetArticleCategoryInfo, GetArticlesByCategory, GetLatestStaffPick } from "../../../Api/Article";
import ArticleDTO from "../../../Models/DTOs/ArticleDTO";
import ArticleCategoryDTO from "../../../Models/DTOs/ArticleCategoryDTO";
import { distinctArticles } from "./useFetchArticleFeed";

const INITIAL_ARTICLE_COUNT = 14;
const PAGE_SIZE = 12;

export default function useFetchArticleCategoryFeed(categoryId: number) {
    const [articles, setArticles] = useState<ArticleDTO[]>([]);
    const [featuredArticle, setFeaturedArticle] = useState<ArticleDTO | null>(null);
    const [featuredIsStaffPick, setFeaturedIsStaffPick] = useState(false);
    const [category, setCategory] = useState<ArticleCategoryDTO | null>(null);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState("");
    const [loadMoreError, setLoadMoreError] = useState(false);
    const [revision, setRevision] = useState(0);
    const request = useRef<{ controller: AbortController; skip: number; busy: boolean; featuredId?: number } | null>(null);

    useEffect(() => {
        const controller = new AbortController();
        const active = { controller, skip: 0, busy: true, featuredId: undefined as number | undefined };
        request.current = active;
        setArticles([]);
        setFeaturedArticle(null);
        setFeaturedIsStaffPick(false);
        setCategory(null);
        setHasMore(false);
        setError("");
        setLoadMoreError(false);
        setLoading(true);

        async function load() {
            if (!Number.isSafeInteger(categoryId) || categoryId <= 0) {
                setError("This article category could not be found.");
                setLoading(false);
                active.busy = false;
                return;
            }
            const [articlesResult, featuredResult, categoryResult] = await Promise.allSettled([
                GetArticlesByCategory(categoryId, 0, INITIAL_ARTICLE_COUNT, controller),
                GetLatestStaffPick(controller, categoryId),
                GetArticleCategoryInfo(categoryId, controller),
            ]);
            if (controller.signal.aborted) return;
            const page = articlesResult.status === "fulfilled" ? articlesResult.value.items : [];
            const promoted = featuredResult.status === "fulfilled" ? featuredResult.value.items[0] : undefined;
            const lead = promoted ?? page[0] ?? null;
            // The lead plus 13 regular stories and the advert fill the initial rows.
            const regularArticles = distinctArticles(page, lead ? [lead.id] : [])
                .slice(0, INITIAL_ARTICLE_COUNT - 1);
            active.featuredId = lead?.id;
            active.skip = regularArticles.length;
            setFeaturedArticle(lead);
            setFeaturedIsStaffPick(!!promoted);
            setArticles(regularArticles);
            setCategory(categoryResult.status === "fulfilled" ? categoryResult.value : null);
            // API count is the size of this page, not a total. A short page ends browsing.
            setHasMore(page.length === INITIAL_ARTICLE_COUNT);
            if ([articlesResult, featuredResult, categoryResult].some(result => result.status === "rejected")) {
                setError("Some articles or category details could not be loaded. Please try again.");
            }
            active.busy = false;
            setLoading(false);
        }
        void load();
        return () => controller.abort();
    }, [categoryId, revision]);

    const loadMore = useCallback(async () => {
        const active = request.current;
        if (!active || active.busy || active.controller.signal.aborted || !hasMore) return;
        active.busy = true;
        setLoading(true);
        setLoadMoreError(false);
        try {
            const page = await GetArticlesByCategory(categoryId, active.skip, PAGE_SIZE, active.controller,
                active.featuredId === undefined ? [] : [active.featuredId]);
            if (active.controller.signal.aborted) return;
            // Exclude the lead before paging and count only displayed regular stories,
            // so an older Staff pick cannot displace a story or leave a gap.
            active.skip += page.items.length;
            setArticles(previous => distinctArticles([...previous, ...page.items],
                active.featuredId === undefined ? [] : [active.featuredId]));
            setHasMore(page.items.length === PAGE_SIZE);
        } catch {
            if (!active.controller.signal.aborted) setLoadMoreError(true);
        } finally {
            if (!active.controller.signal.aborted) {
                active.busy = false;
                setLoading(false);
            }
        }
    }, [categoryId, hasMore]);

    return { articles, featuredArticle, featuredIsStaffPick, category, loading, hasMore, loadMore,
        error: loadMoreError ? "More articles could not be loaded. Select Load more or Try again to retry." : error,
        retry: () => loadMoreError ? loadMore() : setRevision(value => value + 1) };
}